import { type FC, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { languageSelector } from "@/src/contexts";
import FlyerCatalogueModal from "@/src/flyers/FlyerCatalogue/FlyerCatalogueModal";
import ImageLoader from "@Components/ImageLoader";
import { onDefaultKeyboardUp } from "@Components/Modules/Helpers";
import { flyerPageQueryParam } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import {
    type QueryMdMaxToMdMin,
    type QuerySmMax,
    type QuerySmMaxToSmMin,
    type QueryXsMax,
    colors,
} from "../../../styles/tokens.stylex";
import { FlyerCatalogueResources } from "./FlyerCatalogue.Resources";
import { FlyerCatalogueResourcesEn } from "./FlyerCatalogue.Resources.En";
import { FlyerDisplayPosition, type IFlyerCatalogueImage } from "./FlyerCatalogueHelpers";

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const smMax: QuerySmMax = "@media (max-width: 989px)";
const mdMaxToMdMin: QueryMdMaxToMdMin = "@media (max-width: 1239px) and (min-width: 990px)";
const smMaxToSmMin: QuerySmMaxToSmMin = "@media (max-width: 989px) and (min-width: 760px)";

const styles = stylex.create({
    flyerCatalogueComponent: {
        height: "100%",
    },

    gridView: {
        columnGap: {
            default: "32px",
            [mdMaxToMdMin]: "24px",
            [smMax]: "20px",
        },
        display: "grid",
        gridAutoRows: "1fr",
        gridTemplateColumns: {
            default: "repeat(6, 1fr)",
            [mdMaxToMdMin]: "repeat(5, 1fr)",
            [smMax]: "repeat(2, 1fr)",
        },
        rowGap: {
            default: "32px",
            [mdMaxToMdMin]: "24px",
            [smMax]: "20px",
        },
    },

    gridItem: {
        borderColor: colors.b_centas,
        borderStyle: "solid",
        borderWidth: "1px",
        alignItems: "center",

        cursor: "pointer",
        display: "inline-grid",
        justifySelf: "center",

        position: "relative",
        minHeight: { default: "200px", [smMaxToSmMin]: "400px", [xsMax]: "180px" },
        width: "100%",
    },

    gridFooter: {
        backgroundColor: colors.b_Emphasis,

        color: colors.grayDark,
        fontSize: "0.75rem",
        position: "absolute",

        textAlign: "center",
        borderBottomLeftRadius: "0",
        borderBottomRightRadius: "0",
        borderTopLeftRadius: "2px",
        borderTopRightRadius: "0",
        bottom: "0",
        right: "0",
        width: "36px",
    },

    image: {
        height: "100%",
        width: "100%",
    },
});

export type IFlyerCatalogue = {
    pages: IFlyerCatalogueImage[];
    flyerId: string;
    canonicalUrl: string;
    brandName?: string;
    brandUrlSeo: string;
    validityTitle?: string;
    hideNavigation?: boolean;
    title: string;
    caption: string;
    shareUrl: string;
};

const createMagazine = (
    inputArray: IFlyerCatalogueImage[],
    isSearchNavigation: boolean,
): IFlyerCatalogueImage[] => {
    const result: IFlyerCatalogueImage[] = [
        {
            ...inputArray[0],
            carouselPosition: 0,
            displayPosition: FlyerDisplayPosition.Center,
        },
    ];

    let subarray: IFlyerCatalogueImage[] = [];
    let addIncrement = 0;
    for (let i = 1; i < inputArray.length; i += 1) {
        const currentItem = inputArray[i];

        if (currentItem.isBanner) {
            if (isSearchNavigation) {
                addIncrement++;
                continue;
            }
            if (subarray.length === 1) {
                result.push({
                    ...subarray[0],
                    carouselPosition: i - 1 - addIncrement,
                    displayPosition: FlyerDisplayPosition.Center,
                });
            }
            result.push({
                ...inputArray[i],
                carouselPosition: i - addIncrement,
                displayPosition: FlyerDisplayPosition.Center,
            });
            subarray = [];
        } else {
            subarray.push({
                ...inputArray[i],
                carouselPosition: i - addIncrement,
                displayPosition:
                    subarray.length === 0 ? FlyerDisplayPosition.Left : FlyerDisplayPosition.Right,
            });
        }

        if (subarray.length === 2) {
            result.push(...subarray);
            subarray = [];
        }
    }

    if (subarray.length > 0) {
        result.push({
            ...subarray[0],
            displayPosition: FlyerDisplayPosition.Center,
        });
    }

    return result;
};

export const FlyerCatalogue: FC<IFlyerCatalogue> = (props: IFlyerCatalogue) => {
    const resources = languageSelector(FlyerCatalogueResources, FlyerCatalogueResourcesEn);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isSearchNavigation = !!searchParams.get("navigationKey");

    const magazine = useMemo(
        () => createMagazine(props.pages, isSearchNavigation),
        [props.pages, isSearchNavigation],
    );

    const onlyFlyers = useMemo(() => props.pages.filter((flyer) => !flyer.isBanner), [props.pages]);
    const urlPageNumberParam = Number(searchParams.get(flyerPageQueryParam));

    let urlPageNumber = -1;

    if (urlPageNumberParam > 0 && urlPageNumber <= onlyFlyers.length) {
        urlPageNumber = urlPageNumberParam;
    }

    const getMagazineIndexFromPageNumber = props.pages.findIndex((m) => m.pageNumber === urlPageNumber);

    const navigateUrlReturn = () => {
        const returnUrl = searchParams.get("return");
        if (returnUrl) {
            navigate(returnUrl);
            return false;
        }
        return true;
    };

    const openFlyersModal = (pageNumber: number): void => {
        searchParams.set(flyerPageQueryParam, `${pageNumber}`);
        setSearchParams(searchParams, { replace: true });
    };

    const onModalClose = () => {
        searchParams.delete(flyerPageQueryParam);
        setSearchParams(searchParams, { replace: true });
    };

    return (
        <div {...stylex.props(styles.flyerCatalogueComponent)}>
            {urlPageNumber >= 0 && (
                <FlyerCatalogueModal
                    magazine={magazine}
                    flyerId={props.flyerId}
                    resources={resources}
                    onModalClose={onModalClose}
                    magazineIndex={getMagazineIndexFromPageNumber}
                    totalPages={onlyFlyers.length}
                    title={props.title}
                    caption={props.caption}
                    shareUrl={props.canonicalUrl}
                    hideNavigation={props.hideNavigation}
                    brandName={props.brandName}
                    brandUrlSeo={props.brandUrlSeo}
                    validityTitle={props.validityTitle}
                    onGoBack={navigateUrlReturn}
                />
            )}
            <div {...stylex.props(styles.gridView)}>
                {onlyFlyers.map((item) => (
                    <button
                        key={item.pageNumber}
                        {...stylex.props(styles.gridItem)}
                        onClick={() => openFlyersModal(item.pageNumber)}
                        onKeyUp={onDefaultKeyboardUp}
                        type="button"
                        aria-label="open flyer"
                    >
                        <ImageLoader
                            src={item.image.src}
                            srcSetAxis="height"
                            size={300}
                            alt={`${item.pageNumber}`}
                            bypassFormats={true}
                            {...stylex.props(styles.image)}
                        />
                        <span {...stylex.props(styles.gridFooter)}>{item.pageNumber}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

FlyerCatalogue.displayName = "FlyerCatalogue";
